/**
 * AppTopBar -- Shared top bar for all areas (chat, code, admin).
 *
 * Renders a fixed-height header with optional left content slot
 * and right-side utilities: quota bar, language switcher, theme toggle, user menu.
 */

import { Suspense, lazy, type ReactNode } from 'react';
import { LanguageSwitcher } from '@/components/ui/LanguageSwitcher';
import { ThemeToggle } from '@/components/ui/ThemeToggle';
import { UserMenu } from './UserMenu';
import { cn } from '@/utils/cn';

// ---------------------------------------------------------------------------
// Lazy-load HeaderQuotaBar -- silently render nothing on error or if unauthed
// ---------------------------------------------------------------------------

const LazyHeaderQuotaBar = lazy(() =>
  import('./HeaderQuotaBar').then((mod) => ({ default: mod.HeaderQuotaBar })),
);

function QuotaBarFallback() {
  return null;
}

function SafeQuotaBar() {
  return (
    <Suspense fallback={<QuotaBarFallback />}>
      <QuotaBarErrorBoundary>
        <LazyHeaderQuotaBar />
      </QuotaBarErrorBoundary>
    </Suspense>
  );
}

// ---------------------------------------------------------------------------
// Minimal error boundary for HeaderQuotaBar (renders nothing on error)
// ---------------------------------------------------------------------------

import { Component, type ErrorInfo } from 'react';

interface EBProps {
  children: ReactNode;
}

interface EBState {
  hasError: boolean;
}

class QuotaBarErrorBoundary extends Component<EBProps, EBState> {
  constructor(props: EBProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): EBState {
    return { hasError: true };
  }

  componentDidCatch(_error: Error, _info: ErrorInfo): void {
    // Silently swallow -- quota bar is non-critical
  }

  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface AppTopBarProps {
  /** Optional content rendered on the left side (e.g. sidebar toggle, breadcrumbs) */
  left?: ReactNode;
  /** Additional CSS classes for the outer header element */
  className?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AppTopBar({ left, className }: AppTopBarProps) {
  return (
    <header
      data-topbar
      className={cn(
        'flex h-[var(--topbar-height)] items-center gap-3',
        'border-b border-border-default bg-bg-secondary px-4',
        'shrink-0',
        className,
      )}
    >
      {/* Left slot */}
      <div className="flex-1 min-w-0">{left}</div>

      {/* Right utilities */}
      <div className="flex items-center gap-2 shrink-0">
        <SafeQuotaBar />
        <LanguageSwitcher variant="icon" />
        <ThemeToggle size="md" />
        <UserMenu />
      </div>
    </header>
  );
}
